import { DocumentFolder, FolderDetail, FolderEntry, MemberRole } from '@quick/client';
import { managementApi, memberApi } from '../api';
import { DOCUMENTS_ROOT_PATH } from '../utils/DocumentUtils';
import { useDispatch } from 'react-redux';
import { setFolderTreeRoot } from '../redux/actions';
import { DEFAULT_REFRESH_INTERVAL } from '../constants/ForestConstant';
import { useCurrentActiveMember, useCurrentMember } from '../redux/selector';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useEffect } from 'react';
import { COMMUNITIES_KEY, DETAIL, FOLDER_DETAIL_KEY, FOLDER_TREE_KEY } from '../api/query';

const folderDetailKeys = {
  all: (communityId: number) => [COMMUNITIES_KEY, communityId, FOLDER_DETAIL_KEY],
  details: (communityId: number) => [...folderDetailKeys.all(communityId), DETAIL],
  detail: (communityId: number, folderPath: string) => [...folderDetailKeys.details(communityId), folderPath],
};

const folderTreeKeys = {
  all: (communityId: number) => [COMMUNITIES_KEY, communityId, FOLDER_TREE_KEY],
  details: (communityId: number) => [...folderTreeKeys.all(communityId), DETAIL],
};

export const usePrefetchFolderDetail = (folderPath: string) => {
  const currentMember = useCurrentMember();
  const communityId = currentMember?.communityId;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!communityId) {
      return;
    }
    queryClient.prefetchQuery(folderDetailKeys.detail(communityId, folderPath), () =>
      memberApi.getDocumentFolderDetail({
        communityId,
        path: folderPath,
      })
    );
  }, [communityId, folderPath, queryClient]);
};

export const useFolderDetailQuery = (folderPath: string) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  // Fetch all.
  return useQuery(
    folderDetailKeys.detail(currentMember.communityId, folderPath),
    () =>
      memberApi.getDocumentFolderDetail({
        communityId: currentMember.communityId,
        path: folderPath,
      }),
    {
      refetchOnWindowFocus: true,
      refetchInterval: DEFAULT_REFRESH_INTERVAL,
      keepPreviousData: true,
      enabled: !!currentMember,
    }
  );
};

interface CreateFolderRequest {
  parentFolderPath: string;
  folderName: string;
  visibleToRoles: MemberRole[];
}

interface DeleteFolderRequest {
  parentFolderPath: string;
  entryNames: string[];
}

export const useDocumentsMutation = () => {
  const queryClient = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  const createFolder = useMutation(
    (request: CreateFolderRequest) =>
      managementApi.createDocumentFolder({
        createFolderRequest: {
          communityId: currentMember.communityId,
          path: request.parentFolderPath + '/' + request.folderName,
          visibleToRoles: request.visibleToRoles,
        },
      }),
    {
      async onSuccess(data: DocumentFolder, request: CreateFolderRequest) {
        // Optimistically update the parent folder query result.
        queryClient.setQueryData<FolderDetail | undefined>(
          folderDetailKeys.detail(currentMember.communityId, request.parentFolderPath),
          (old) => {
            if (!old) {
              return old;
            }
            const newEntry: FolderEntry = {
              ...data,
              member: currentMember,
              dateAdded: new Date(),
              dateModified: new Date(),
            };
            return {
              ...old,
              subFolders: [...old.subFolders, newEntry],
            };
          }
        );
        // Invalidate the parent folder query and wait for refetch.
        queryClient.invalidateQueries(folderDetailKeys.detail(currentMember.communityId, request.parentFolderPath));
        queryClient.invalidateQueries(folderTreeKeys.details(currentMember.communityId));
      },
    }
  );
  const batchDelete = useMutation(
    (request: DeleteFolderRequest) =>
      managementApi.batchDelete({
        batchDeleteRequest: {
          communityId: currentMember.communityId,
          paths: request.entryNames.map((it) => request.parentFolderPath + '/' + it),
        },
      }),
    {
      async onSuccess(_, request: DeleteFolderRequest) {
        // Optimistically update the parent folder query result.
        queryClient.setQueryData<FolderDetail | undefined>(
          folderDetailKeys.detail(currentMember.communityId, request.parentFolderPath),
          (old) => {
            if (!old) {
              return old;
            }
            return {
              ...old,
              subFolders: old.subFolders.filter((it) => !request.entryNames.includes(it.name)),
              files: old.files.filter((it) => !request.entryNames.includes(it.name)),
            };
          }
        );
        // Invalidate the parent folder query and wait for refetch.
        queryClient.invalidateQueries(folderDetailKeys.detail(currentMember.communityId, request.parentFolderPath));
        queryClient.invalidateQueries(folderTreeKeys.details(currentMember.communityId));
      },
    }
  );
  return {
    createFolder,
    batchDelete,
  };
};

export const usePrefetchFolderTree = () => {
  const currentMember = useCurrentActiveMember();
  const communityId = currentMember?.communityId;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!communityId) {
      return;
    }
    queryClient.prefetchQuery(folderTreeKeys.details(communityId), () =>
      memberApi.getDocumentFolderTree({
        communityId,
        path: DOCUMENTS_ROOT_PATH,
      })
    );
  }, [communityId, queryClient]);
};

export const useFolderTreeQuery = () => {
  const dispatch = useDispatch();
  const currentMember = useCurrentMember()!;
  return useQuery(
    folderTreeKeys.details(currentMember.communityId),
    async () =>
      memberApi.getDocumentFolderTree({
        communityId: currentMember.communityId,
        path: DOCUMENTS_ROOT_PATH,
      }),
    {
      onSuccess(data) {
        dispatch(setFolderTreeRoot(data));
      },
      refetchOnWindowFocus: true,
      refetchInterval: DEFAULT_REFRESH_INTERVAL,
    }
  );
};
